/* ================== ROLES (single source of truth) ================== */

export type Role =
  | "owner"
  | "super_admin"
  | "admin"
  | "evaluator"
  | "evaluatorTest"
  | "moderator"
  | "instructor"
  | "publisher"
  | "auditor"
  | "user";

export const ROLES = [
  "owner",
  "super_admin",
  "admin",
  "evaluator",
  "evaluatorTest",
  "moderator",
  "instructor",
  "publisher",
  "auditor",
  "user",
] as const satisfies readonly Role[];

/** Etiquetas legibles */
export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  super_admin: "Super Admin",
  admin: "Admin",
  evaluator: "Evaluador",
  evaluatorTest: "Evaluador (prueba)",
  moderator: "Moderador",
  instructor: "Instructor",
  publisher: "Publisher",
  auditor: "Auditor",
  user: "Usuario",
};

/** Ranking jerárquico (mientras más alto, más poder) */
export const ROLE_RANK: Record<Role, number> = {
  user: 0,
  evaluatorTest: 1,
  evaluator: 2,
  moderator: 3,
  instructor: 3,
  publisher: 3,
  auditor: 3,
  admin: 50,
  super_admin: 80,
  owner: 100,
};

export const OWNER_EMAIL = "ing.alex.za@gmail.com";

/* ================== Ayudas ================== */

export function parseRole(v: unknown): Role | undefined {
  return typeof v === "string" && (ROLES as readonly string[]).includes(v)
    ? (v as Role)
    : undefined;
}

export function isRole(v: unknown): v is Role {
  return parseRole(v) !== undefined;
}

/** Quién puede administrar (editar) a quién */
export function canManageRole(actor: Role, target: Role | undefined): boolean {
  if (!target) return false;
  if (actor === "owner") return true;
  if (actor === "super_admin") return ROLE_RANK[target] < ROLE_RANK.super_admin;
  if (actor === "admin") return ROLE_RANK[target] < ROLE_RANK.admin;
  return false;
}

/** Qué roles puede asignar cada actor */
export function canAssignRole(actor: Role, next: Role): boolean {
  if (actor === "owner") return next !== "owner";
  if (actor === "super_admin") return ROLE_RANK[next] <= ROLE_RANK.admin;
  if (actor === "admin") return ROLE_RANK[next] < ROLE_RANK.admin;
  return false;
}

/* ================== Grupos reusables ================== */

export const ANY_AUTH = ROLES;

export const ADMIN_ROLES = [
  "owner",
  "super_admin",
  "admin",
] as const satisfies readonly Role[];

/** Acceso total al dashboard */
export const DASHBOARD_FULL = ADMIN_ROLES;

/** Secciones específicas (admin + rol operativo) */
export const COURSES_ROLES = [
  ...ADMIN_ROLES,
  "instructor",
] as const satisfies readonly Role[];

export const QUESTIONNAIRES_ROLES = [
  ...ADMIN_ROLES,
  "auditor",
] as const satisfies readonly Role[];

export const CERTIFICATIONS_ROLES = [
  ...ADMIN_ROLES,
  "auditor",
] as const satisfies readonly Role[];

/** Quien puede moderar comunidad (ver botones/moderar) */
export const COMMUNITY_MOD_ROLES = [
  ...ADMIN_ROLES,
  "moderator",
] as const satisfies readonly Role[];

/** Rutas que muestran estadísticas/lecturas para auditores */
export const REPORTS_ROLES = [
  ...ADMIN_ROLES,
  "auditor",
] as const satisfies readonly Role[];
